import React, { useState } from 'react';
import { Box, Flex, Grid, Skeleton } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Context } from '../App';
import { RoutePath, SPACING } from '../generic/config';
import Icon, { Icons } from '../base-components/Icons';
import Text from '../base-components/Text';
import { QueryKeys } from '../api/base/react-query';
import { getDashboardStats } from '../api/dashboard-controller';
import { getDailyCounts } from '../api/order-controller';
import { useColorMode } from '../components/ui/color-mode';
import { useDisplayName } from '../base-components/Header';
import { capitalizeFirstLetter } from '../generic/string-utils';
import { Language } from '../generic/Translations';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';

/**
 * Order lifecycle in a fixed order, each with a fixed categorical slot (colour
 * follows the status, never its rank). Validated with the dataviz palette check
 * in both modes; the light slots under 3:1 are always shown with their name and
 * count beside them, never colour alone.
 */
const STATUS_ORDER = ["OFFERED", "CREATED", "COMPLETED", "INVOICED", "CANCELLED"] as const;
type Status = typeof STATUS_ORDER[number];
const STATUS_COLOR: Record<Status, { light: string; dark: string }> = {
    OFFERED: { light: "#2a78d6", dark: "#3987e5" },
    CREATED: { light: "#eb6834", dark: "#d95926" },
    COMPLETED: { light: "#1baf7a", dark: "#199e70" },
    INVOICED: { light: "#eda100", dark: "#c98500" },
    CANCELLED: { light: "#e87ba4", dark: "#d55181" },
};
/** One hue for magnitude bars (sequential blue). */
const BAR_COLOR = { light: "#2a78d6", dark: "#3987e5" };

const nf = (lang: Language) => new Intl.NumberFormat(lang === Language.SE ? "sv-SE" : "en-US");
const pct = (part: number, whole: number, lang: Language) =>
    whole > 0 ? new Intl.NumberFormat(lang === Language.SE ? "sv-SE" : "en-US", { style: "percent", maximumFractionDigits: 0 }).format(part / whole) : "–";

const dayPart = (d: Date): "morning" | "day" | "evening" => d.getHours() < 10 ? "morning" : d.getHours() < 18 ? "day" : "evening";

const Panel = ({ title, description, children, testId }: { title: string; description?: string; children: React.ReactNode; testId?: string }) => (
    <Box borderWidth="1px" borderRadius="lg" bg="bg.panel" p={SPACING * 2.5} textAlign="start" data-testid={testId}>
        <Text fontWeight="600" fontSize="md">{title}</Text>
        {description && <Text fontSize="sm" color="fg.muted" mt={1}>{description}</Text>}
        <Box mt={SPACING * 2.5}>{children}</Box>
    </Box>
);

const KpiTile = ({ label, value, sub, icon, to, loading, testId }: { label: string; value: string; sub?: string; icon: IconDefinition; to?: string; loading?: boolean; testId?: string }) => {
    const body = (
        <Box borderWidth="1px" borderRadius="lg" bg="bg.panel" p={SPACING * 2.5} textAlign="start" h="100%"
            transition="border-color 0.15s" _hover={to ? { borderColor: "border.emphasized" } : undefined} data-testid={testId}>
            <Flex alignItems="center" gap={SPACING} color="fg.muted">
                <Icon icon={icon} size="sm" />
                <Text fontSize="sm" fontWeight="500" color="fg.muted">{label}</Text>
            </Flex>
            <Skeleton loading={!!loading} mt={SPACING * 1.5}>
                <Text fontSize="3xl" fontWeight="600" letterSpacing="-0.02em" lineHeight="1" fontVariantNumeric="tabular-nums">{value}</Text>
            </Skeleton>
            {sub && <Text fontSize="xs" color="fg.muted" mt={SPACING}>{sub}</Text>}
        </Box>
    );
    return to ? <Link to={to} style={{ display: "block", height: "100%" }}>{body}</Link> : body;
};

const DashboardPage: React.FC = () => {
    const { translations: t, language, companySettings } = React.useContext(Context);
    const d = t.dashboard;
    const { colorMode } = useColorMode();
    const mode = colorMode === "dark" ? "dark" : "light";
    const name = useDisplayName();
    const { data, isLoading } = useQuery(QueryKeys.dashboard, getDashboardStats);

    const today = new Date();
    const todayKey = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    const todayQuery = useQuery(['dailyCounts', 'today', todayKey.toISOString()],
        () => getDailyCounts(todayKey, todayKey, null, false));
    const todayCount = todayQuery.data?.reduce((sum, c) => sum + c.count, 0) ?? 0;

    const locale = language === Language.SE ? "sv-SE" : "en-US";
    const dateLine = capitalizeFirstLetter(today.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" }));
    const companyName = companySettings?.data?.name;
    const n = nf(language);

    const byStatus = (s: Status) => data?.ordersByStatus?.[s] ?? 0;
    const totalOrders = data?.totalOrders ?? 0;
    const statusTotal = STATUS_ORDER.reduce((sum, s) => sum + byStatus(s), 0);

    return (
        <Box textAlign="start" px={{ base: SPACING, md: SPACING * 2 }} py={SPACING * 2} maxW="1400px">
            {/* Greeting */}
            <Flex alignItems={{ base: "start", md: "end" }} direction={{ base: "column", md: "row" }} gap={SPACING} mb={SPACING * 4}>
                <Box>
                    <Text fontSize="2xl" fontWeight="600" letterSpacing="-0.02em" data-testid="dashboard-greeting">
                        {d.greeting(dayPart(today), name.first)}
                    </Text>
                    <Flex gap={SPACING} alignItems="center" color="fg.muted" mt={1} fontSize="sm" wrap="wrap">
                        {companyName && <><Icon icon={Icons.company} size="xs" /><Text fontSize="sm" color="fg.muted">{companyName}</Text><Text fontSize="sm" color="fg.subtle">·</Text></>}
                        <Text fontSize="sm" color="fg.muted">{dateLine}</Text>
                    </Flex>
                </Box>
            </Flex>

            {/* KPI row */}
            <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, 1fr)", xl: "repeat(5, 1fr)" }} gap={SPACING * 2} mb={SPACING * 3}>
                <KpiTile testId="kpi-today" label={d.todayTasks} value={n.format(todayCount)} sub={d.todayTasksSubtitle} icon={Icons.Task} to={RoutePath.Tasks} loading={todayQuery.isLoading} />
                <KpiTile label={d.totalOrders} value={n.format(totalOrders)} sub={d.openTasks + ": " + n.format(byStatus("CREATED"))} icon={Icons.Order} to={RoutePath.Orders} loading={isLoading} />
                <KpiTile label={d.completedOrders} value={n.format(byStatus("COMPLETED"))} sub={d.ofAllOrders(pct(byStatus("COMPLETED"), totalOrders, language))} icon={Icons.check} loading={isLoading} />
                <KpiTile label={d.totalWorksites} value={n.format(data?.totalWorksites ?? 0)} sub={d.totalWorksitesSubtitle} icon={Icons.Worksite} to={RoutePath.Worksites} loading={isLoading} />
                <KpiTile label={d.totalCustomers} value={n.format(data?.totalCustomers ?? 0)} sub={d.totalCustomersSubtitle} icon={Icons.Customer} to={RoutePath.Customers} loading={isLoading} />
            </Grid>

            <Grid templateColumns={{ base: "1fr", lg: "3fr 2fr" }} gap={SPACING * 2}>
                <Panel title={d.ordersByStatus.title} description={d.ordersByStatus.description} testId="panel-status">
                    {isLoading ? <Skeleton h="140px" /> : statusTotal === 0 ? <Text color="fg.muted">{d.noData}</Text> : (
                        <StatusBreakdown mode={mode} counts={STATUS_ORDER.map(s => ({ status: s, count: byStatus(s) }))} total={statusTotal} />
                    )}
                </Panel>
                <Panel title={d.completedJobsPerAgent.title} description={d.completedJobsPerAgent.description} testId="panel-resources">
                    {isLoading ? <Skeleton h="140px" /> : (data?.completedPerResource?.length ?? 0) === 0 ? <Text color="fg.muted">{d.noResourceData}</Text> : (
                        <RankedBars mode={mode} rows={[...data!.completedPerResource].sort((a, b) => b.count - a.count)} />
                    )}
                </Panel>
            </Grid>
        </Box>
    );
};

/** Thin segmented bar (2px surface gaps) above a list that names each status with its count and share. */
const StatusBreakdown = ({ counts, total, mode }: { counts: { status: Status; count: number }[]; total: number; mode: "light" | "dark" }) => {
    const { translations: t, language } = React.useContext(Context);
    const [hover, setHover] = useState<Status | null>(null);
    const n = nf(language);
    const label = (s: Status) => t.model.order.statusOptions[s];
    const visible = counts.filter(c => c.count > 0);

    return (
        <Box>
            <Flex h="10px" gap="2px" borderRadius="4px" overflow="hidden" role="img"
                aria-label={visible.map(c => `${label(c.status)} ${c.count}`).join(", ")} data-testid="status-bar">
                {visible.map(c => (
                    <Box key={c.status} flex={c.count} minW="3px" bg={STATUS_COLOR[c.status][mode]}
                        opacity={hover && hover !== c.status ? 0.35 : 1} transition="opacity 0.12s"
                        onMouseEnter={() => setHover(c.status)} onMouseLeave={() => setHover(null)}
                        title={`${label(c.status)}: ${n.format(c.count)} (${pct(c.count, total, language)})`} />
                ))}
            </Flex>
            <Box mt={SPACING * 2}>
                {counts.map(c => (
                    <Flex key={c.status} alignItems="center" gap={SPACING * 1.5} py={SPACING} px={SPACING} mx={-SPACING} borderRadius="md"
                        bg={hover === c.status ? "bg.muted" : undefined}
                        onMouseEnter={() => setHover(c.status)} onMouseLeave={() => setHover(null)}
                        data-testid={`status-row-${c.status}`}>
                        <Box w="10px" h="10px" borderRadius="3px" bg={STATUS_COLOR[c.status][mode]} flexShrink={0} />
                        <Text fontSize="sm" flex={1}>{label(c.status)}</Text>
                        <Text fontSize="sm" fontWeight="600" fontVariantNumeric="tabular-nums">{n.format(c.count)}</Text>
                        <Text fontSize="sm" color="fg.muted" w="3.5rem" textAlign="end" fontVariantNumeric="tabular-nums">{pct(c.count, total, language)}</Text>
                    </Flex>
                ))}
                <Flex alignItems="center" gap={SPACING * 1.5} pt={SPACING} mt={SPACING} borderTopWidth="1px">
                    <Box w="10px" flexShrink={0} />
                    <Text fontSize="sm" flex={1} color="fg.muted">{t.dashboard.total}</Text>
                    <Text fontSize="sm" fontWeight="600" fontVariantNumeric="tabular-nums">{n.format(total)}</Text>
                    <Box w="3.5rem" />
                </Flex>
            </Box>
        </Box>
    );
};

/** Ranked horizontal bars, one hue, value printed at the bar's end; hover highlights the row. */
const RankedBars = ({ rows, mode }: { rows: { resourceCode: string; count: number }[]; mode: "light" | "dark" }) => {
    const { translations: t, language } = React.useContext(Context);
    const [hover, setHover] = useState<string | null>(null);
    const n = nf(language);
    const max = Math.max(1, ...rows.map(r => r.count));

    return (
        <Box>
            {rows.map(r => (
                <Box key={r.resourceCode} py={SPACING} onMouseEnter={() => setHover(r.resourceCode)} onMouseLeave={() => setHover(null)}
                    title={`${r.resourceCode}: ${t.dashboard.orders(r.count)}`} data-testid={`resource-row-${r.resourceCode}`}>
                    <Flex justifyContent="space-between" mb={1.5}>
                        <Text fontSize="sm">{r.resourceCode}</Text>
                        <Text fontSize="sm" fontWeight="600" fontVariantNumeric="tabular-nums">{n.format(r.count)}</Text>
                    </Flex>
                    <Box h="8px" borderRadius="4px" bg="bg.muted">
                        <Box h="100%" w={`${(r.count / max) * 100}%`} minW="4px" borderRadius="4px" bg={BAR_COLOR[mode]}
                            opacity={hover && hover !== r.resourceCode ? 0.45 : 1} transition="opacity 0.12s" />
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default DashboardPage;
